import { useState, useMemo, useRef, useEffect } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';

/**
 * Timezone option interface
 */
interface TimezoneOption {
  value: string;
  label: string;
  region: 'Americas' | 'Europe' | 'Asia' | 'Australia' | 'Africa' | 'Other';
  searchTerms: string[];
}

/**
 * TimezoneSelect component props
 */
export interface TimezoneSelectProps {
  value: string | null;
  onChange: (timezone: string) => void;
  label?: string;
  placeholder?: string;
  showCurrentTime?: boolean;
  className?: string;
  disabled?: boolean;
}

/**
 * Comprehensive list of major IANA timezones organized by region
 */
const TIMEZONES: TimezoneOption[] = [
  // Americas
  { value: 'America/New_York', label: 'New York', region: 'Americas', searchTerms: ['new york', 'est', 'edt', 'eastern', 'ny'] },
  { value: 'America/Chicago', label: 'Chicago', region: 'Americas', searchTerms: ['chicago', 'cst', 'cdt', 'central'] },
  { value: 'America/Los_Angeles', label: 'Los Angeles', region: 'Americas', searchTerms: ['los angeles', 'pst', 'pdt', 'pacific', 'la'] },
  { value: 'America/Denver', label: 'Denver', region: 'Americas', searchTerms: ['denver', 'mst', 'mdt', 'mountain'] },
  { value: 'America/Phoenix', label: 'Phoenix', region: 'Americas', searchTerms: ['phoenix', 'mst', 'arizona'] },
  { value: 'America/Anchorage', label: 'Anchorage', region: 'Americas', searchTerms: ['anchorage', 'akst', 'akdt', 'alaska'] },
  { value: 'America/Toronto', label: 'Toronto', region: 'Americas', searchTerms: ['toronto', 'est', 'edt', 'canada'] },
  { value: 'America/Vancouver', label: 'Vancouver', region: 'Americas', searchTerms: ['vancouver', 'pst', 'pdt', 'canada'] },
  { value: 'America/Montreal', label: 'Montreal', region: 'Americas', searchTerms: ['montreal', 'est', 'edt', 'canada'] },
  { value: 'America/Halifax', label: 'Halifax', region: 'Americas', searchTerms: ['halifax', 'ast', 'adt', 'canada'] },
  { value: 'America/Mexico_City', label: 'Mexico City', region: 'Americas', searchTerms: ['mexico city', 'cst', 'cdt', 'mexico'] },
  { value: 'America/Bogota', label: 'Bogota', region: 'Americas', searchTerms: ['bogota', 'colombia'] },
  { value: 'America/Lima', label: 'Lima', region: 'Americas', searchTerms: ['lima', 'peru'] },
  { value: 'America/Santiago', label: 'Santiago', region: 'Americas', searchTerms: ['santiago', 'chile'] },
  { value: 'America/Buenos_Aires', label: 'Buenos Aires', region: 'Americas', searchTerms: ['buenos aires', 'argentina'] },
  { value: 'America/Sao_Paulo', label: 'São Paulo', region: 'Americas', searchTerms: ['sao paulo', 'brazil', 'brt'] },
  { value: 'America/Caracas', label: 'Caracas', region: 'Americas', searchTerms: ['caracas', 'venezuela'] },
  { value: 'America/Panama', label: 'Panama City', region: 'Americas', searchTerms: ['panama', 'panama city'] },
  { value: 'America/Costa_Rica', label: 'San José', region: 'Americas', searchTerms: ['costa rica', 'san jose'] },
  { value: 'America/Guatemala', label: 'Guatemala City', region: 'Americas', searchTerms: ['guatemala'] },
  { value: 'America/Havana', label: 'Havana', region: 'Americas', searchTerms: ['havana', 'cuba'] },
  { value: 'America/Jamaica', label: 'Kingston', region: 'Americas', searchTerms: ['jamaica', 'kingston'] },
  { value: 'America/Port_of_Spain', label: 'Port of Spain', region: 'Americas', searchTerms: ['trinidad', 'port of spain'] },
  { value: 'America/Barbados', label: 'Barbados', region: 'Americas', searchTerms: ['barbados'] },

  // Europe
  { value: 'Europe/London', label: 'London', region: 'Europe', searchTerms: ['london', 'gmt', 'bst', 'uk', 'england'] },
  { value: 'Europe/Paris', label: 'Paris', region: 'Europe', searchTerms: ['paris', 'cet', 'cest', 'france'] },
  { value: 'Europe/Berlin', label: 'Berlin', region: 'Europe', searchTerms: ['berlin', 'cet', 'cest', 'germany'] },
  { value: 'Europe/Amsterdam', label: 'Amsterdam', region: 'Europe', searchTerms: ['amsterdam', 'cet', 'cest', 'netherlands'] },
  { value: 'Europe/Brussels', label: 'Brussels', region: 'Europe', searchTerms: ['brussels', 'cet', 'cest', 'belgium'] },
  { value: 'Europe/Madrid', label: 'Madrid', region: 'Europe', searchTerms: ['madrid', 'cet', 'cest', 'spain'] },
  { value: 'Europe/Barcelona', label: 'Barcelona', region: 'Europe', searchTerms: ['barcelona', 'cet', 'cest', 'spain'] },
  { value: 'Europe/Rome', label: 'Rome', region: 'Europe', searchTerms: ['rome', 'cet', 'cest', 'italy'] },
  { value: 'Europe/Vienna', label: 'Vienna', region: 'Europe', searchTerms: ['vienna', 'cet', 'cest', 'austria'] },
  { value: 'Europe/Zurich', label: 'Zurich', region: 'Europe', searchTerms: ['zurich', 'cet', 'cest', 'switzerland'] },
  { value: 'Europe/Stockholm', label: 'Stockholm', region: 'Europe', searchTerms: ['stockholm', 'cet', 'cest', 'sweden'] },
  { value: 'Europe/Copenhagen', label: 'Copenhagen', region: 'Europe', searchTerms: ['copenhagen', 'cet', 'cest', 'denmark'] },
  { value: 'Europe/Oslo', label: 'Oslo', region: 'Europe', searchTerms: ['oslo', 'cet', 'cest', 'norway'] },
  { value: 'Europe/Helsinki', label: 'Helsinki', region: 'Europe', searchTerms: ['helsinki', 'eet', 'eest', 'finland'] },
  { value: 'Europe/Warsaw', label: 'Warsaw', region: 'Europe', searchTerms: ['warsaw', 'cet', 'cest', 'poland'] },
  { value: 'Europe/Prague', label: 'Prague', region: 'Europe', searchTerms: ['prague', 'cet', 'cest', 'czech'] },
  { value: 'Europe/Budapest', label: 'Budapest', region: 'Europe', searchTerms: ['budapest', 'cet', 'cest', 'hungary'] },
  { value: 'Europe/Athens', label: 'Athens', region: 'Europe', searchTerms: ['athens', 'eet', 'eest', 'greece'] },
  { value: 'Europe/Istanbul', label: 'Istanbul', region: 'Europe', searchTerms: ['istanbul', 'turkey'] },
  { value: 'Europe/Moscow', label: 'Moscow', region: 'Europe', searchTerms: ['moscow', 'msk', 'russia'] },
  { value: 'Europe/Dublin', label: 'Dublin', region: 'Europe', searchTerms: ['dublin', 'gmt', 'ist', 'ireland'] },
  { value: 'Europe/Lisbon', label: 'Lisbon', region: 'Europe', searchTerms: ['lisbon', 'wet', 'west', 'portugal'] },
  { value: 'Europe/Bucharest', label: 'Bucharest', region: 'Europe', searchTerms: ['bucharest', 'eet', 'eest', 'romania'] },
  { value: 'Europe/Sofia', label: 'Sofia', region: 'Europe', searchTerms: ['sofia', 'eet', 'eest', 'bulgaria'] },
  { value: 'Europe/Kiev', label: 'Kyiv', region: 'Europe', searchTerms: ['kyiv', 'kiev', 'eet', 'eest', 'ukraine'] },
  { value: 'Europe/Minsk', label: 'Minsk', region: 'Europe', searchTerms: ['minsk', 'belarus'] },
  { value: 'Europe/Reykjavik', label: 'Reykjavik', region: 'Europe', searchTerms: ['reykjavik', 'iceland', 'gmt'] },

  // Asia
  { value: 'Asia/Dubai', label: 'Dubai', region: 'Asia', searchTerms: ['dubai', 'uae', 'gulf'] },
  { value: 'Asia/Karachi', label: 'Karachi', region: 'Asia', searchTerms: ['karachi', 'pakistan', 'pkt'] },
  { value: 'Asia/Kolkata', label: 'Mumbai/Kolkata', region: 'Asia', searchTerms: ['mumbai', 'kolkata', 'india', 'ist', 'delhi', 'bangalore'] },
  { value: 'Asia/Dhaka', label: 'Dhaka', region: 'Asia', searchTerms: ['dhaka', 'bangladesh', 'bdt'] },
  { value: 'Asia/Bangkok', label: 'Bangkok', region: 'Asia', searchTerms: ['bangkok', 'thailand', 'ict'] },
  { value: 'Asia/Jakarta', label: 'Jakarta', region: 'Asia', searchTerms: ['jakarta', 'indonesia', 'wib'] },
  { value: 'Asia/Singapore', label: 'Singapore', region: 'Asia', searchTerms: ['singapore', 'sgt'] },
  { value: 'Asia/Kuala_Lumpur', label: 'Kuala Lumpur', region: 'Asia', searchTerms: ['kuala lumpur', 'malaysia', 'myt'] },
  { value: 'Asia/Manila', label: 'Manila', region: 'Asia', searchTerms: ['manila', 'philippines', 'pht'] },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong', region: 'Asia', searchTerms: ['hong kong', 'hkt'] },
  { value: 'Asia/Shanghai', label: 'Shanghai', region: 'Asia', searchTerms: ['shanghai', 'beijing', 'china', 'cst'] },
  { value: 'Asia/Taipei', label: 'Taipei', region: 'Asia', searchTerms: ['taipei', 'taiwan', 'cst'] },
  { value: 'Asia/Tokyo', label: 'Tokyo', region: 'Asia', searchTerms: ['tokyo', 'japan', 'jst'] },
  { value: 'Asia/Seoul', label: 'Seoul', region: 'Asia', searchTerms: ['seoul', 'south korea', 'kst'] },
  { value: 'Asia/Riyadh', label: 'Riyadh', region: 'Asia', searchTerms: ['riyadh', 'saudi arabia', 'ast'] },
  { value: 'Asia/Jerusalem', label: 'Jerusalem', region: 'Asia', searchTerms: ['jerusalem', 'israel', 'ist'] },
  { value: 'Asia/Tehran', label: 'Tehran', region: 'Asia', searchTerms: ['tehran', 'iran', 'irst'] },
  { value: 'Asia/Baghdad', label: 'Baghdad', region: 'Asia', searchTerms: ['baghdad', 'iraq'] },
  { value: 'Asia/Kuwait', label: 'Kuwait City', region: 'Asia', searchTerms: ['kuwait'] },
  { value: 'Asia/Muscat', label: 'Muscat', region: 'Asia', searchTerms: ['muscat', 'oman'] },
  { value: 'Asia/Doha', label: 'Doha', region: 'Asia', searchTerms: ['doha', 'qatar'] },
  { value: 'Asia/Beirut', label: 'Beirut', region: 'Asia', searchTerms: ['beirut', 'lebanon'] },
  { value: 'Asia/Amman', label: 'Amman', region: 'Asia', searchTerms: ['amman', 'jordan'] },
  { value: 'Asia/Damascus', label: 'Damascus', region: 'Asia', searchTerms: ['damascus', 'syria'] },
  { value: 'Asia/Yerevan', label: 'Yerevan', region: 'Asia', searchTerms: ['yerevan', 'armenia'] },
  { value: 'Asia/Tbilisi', label: 'Tbilisi', region: 'Asia', searchTerms: ['tbilisi', 'georgia'] },
  { value: 'Asia/Baku', label: 'Baku', region: 'Asia', searchTerms: ['baku', 'azerbaijan'] },
  { value: 'Asia/Kathmandu', label: 'Kathmandu', region: 'Asia', searchTerms: ['kathmandu', 'nepal'] },
  { value: 'Asia/Colombo', label: 'Colombo', region: 'Asia', searchTerms: ['colombo', 'sri lanka'] },
  { value: 'Asia/Yangon', label: 'Yangon', region: 'Asia', searchTerms: ['yangon', 'myanmar', 'burma'] },
  { value: 'Asia/Ho_Chi_Minh', label: 'Ho Chi Minh City', region: 'Asia', searchTerms: ['ho chi minh', 'saigon', 'vietnam'] },
  { value: 'Asia/Phnom_Penh', label: 'Phnom Penh', region: 'Asia', searchTerms: ['phnom penh', 'cambodia'] },
  { value: 'Asia/Vientiane', label: 'Vientiane', region: 'Asia', searchTerms: ['vientiane', 'laos'] },

  // Australia/Pacific
  { value: 'Australia/Sydney', label: 'Sydney', region: 'Australia', searchTerms: ['sydney', 'australia', 'aest', 'aedt'] },
  { value: 'Australia/Melbourne', label: 'Melbourne', region: 'Australia', searchTerms: ['melbourne', 'australia', 'aest', 'aedt'] },
  { value: 'Australia/Brisbane', label: 'Brisbane', region: 'Australia', searchTerms: ['brisbane', 'australia', 'aest'] },
  { value: 'Australia/Perth', label: 'Perth', region: 'Australia', searchTerms: ['perth', 'australia', 'awst'] },
  { value: 'Australia/Adelaide', label: 'Adelaide', region: 'Australia', searchTerms: ['adelaide', 'australia', 'acst', 'acdt'] },
  { value: 'Australia/Hobart', label: 'Hobart', region: 'Australia', searchTerms: ['hobart', 'australia', 'aest', 'aedt', 'tasmania'] },
  { value: 'Australia/Darwin', label: 'Darwin', region: 'Australia', searchTerms: ['darwin', 'australia', 'acst'] },
  { value: 'Pacific/Auckland', label: 'Auckland', region: 'Australia', searchTerms: ['auckland', 'new zealand', 'nzst', 'nzdt'] },
  { value: 'Pacific/Fiji', label: 'Fiji', region: 'Australia', searchTerms: ['fiji', 'fjt'] },
  { value: 'Pacific/Honolulu', label: 'Honolulu', region: 'Australia', searchTerms: ['honolulu', 'hawaii', 'hst'] },
  { value: 'Pacific/Guam', label: 'Guam', region: 'Australia', searchTerms: ['guam', 'chst'] },
  { value: 'Pacific/Port_Moresby', label: 'Port Moresby', region: 'Australia', searchTerms: ['port moresby', 'papua new guinea'] },
  { value: 'Pacific/Noumea', label: 'Nouméa', region: 'Australia', searchTerms: ['noumea', 'new caledonia'] },
  { value: 'Pacific/Tahiti', label: 'Tahiti', region: 'Australia', searchTerms: ['tahiti', 'french polynesia'] },

  // Africa
  { value: 'Africa/Cairo', label: 'Cairo', region: 'Africa', searchTerms: ['cairo', 'egypt', 'eet'] },
  { value: 'Africa/Johannesburg', label: 'Johannesburg', region: 'Africa', searchTerms: ['johannesburg', 'south africa', 'sast'] },
  { value: 'Africa/Lagos', label: 'Lagos', region: 'Africa', searchTerms: ['lagos', 'nigeria', 'wat'] },
  { value: 'Africa/Nairobi', label: 'Nairobi', region: 'Africa', searchTerms: ['nairobi', 'kenya', 'eat'] },
  { value: 'Africa/Casablanca', label: 'Casablanca', region: 'Africa', searchTerms: ['casablanca', 'morocco', 'wet'] },
  { value: 'Africa/Algiers', label: 'Algiers', region: 'Africa', searchTerms: ['algiers', 'algeria', 'cet'] },
  { value: 'Africa/Tunis', label: 'Tunis', region: 'Africa', searchTerms: ['tunis', 'tunisia', 'cet'] },
  { value: 'Africa/Tripoli', label: 'Tripoli', region: 'Africa', searchTerms: ['tripoli', 'libya', 'eet'] },
  { value: 'Africa/Khartoum', label: 'Khartoum', region: 'Africa', searchTerms: ['khartoum', 'sudan', 'cat'] },
  { value: 'Africa/Addis_Ababa', label: 'Addis Ababa', region: 'Africa', searchTerms: ['addis ababa', 'ethiopia', 'eat'] },
  { value: 'Africa/Dar_es_Salaam', label: 'Dar es Salaam', region: 'Africa', searchTerms: ['dar es salaam', 'tanzania', 'eat'] },
  { value: 'Africa/Kampala', label: 'Kampala', region: 'Africa', searchTerms: ['kampala', 'uganda', 'eat'] },
  { value: 'Africa/Accra', label: 'Accra', region: 'Africa', searchTerms: ['accra', 'ghana', 'gmt'] },
  { value: 'Africa/Dakar', label: 'Dakar', region: 'Africa', searchTerms: ['dakar', 'senegal', 'gmt'] },
  { value: 'Africa/Abidjan', label: 'Abidjan', region: 'Africa', searchTerms: ['abidjan', 'ivory coast', 'gmt'] },
  { value: 'Africa/Kinshasa', label: 'Kinshasa', region: 'Africa', searchTerms: ['kinshasa', 'congo', 'wat'] },
  { value: 'Africa/Luanda', label: 'Luanda', region: 'Africa', searchTerms: ['luanda', 'angola', 'wat'] },
  { value: 'Africa/Maputo', label: 'Maputo', region: 'Africa', searchTerms: ['maputo', 'mozambique', 'cat'] },
  { value: 'Africa/Harare', label: 'Harare', region: 'Africa', searchTerms: ['harare', 'zimbabwe', 'cat'] },

  // Other
  { value: 'UTC', label: 'UTC', region: 'Other', searchTerms: ['utc', 'gmt', 'coordinated universal time'] },
];

const getTimezoneOffset = (timezone: string): string => {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    });
    const parts = formatter.formatToParts(now);
    const offsetPart = parts.find((p) => p.type === 'timeZoneName');
    if (offsetPart?.value && offsetPart.value.startsWith('GMT')) {
      const offset = offsetPart.value.replace('GMT', 'UTC');
      return offset === 'UTC' ? '(UTC+00:00)' : `(${offset})`;
    }
    const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
    const offsetMinutes = (tzDate.getTime() - utcDate.getTime()) / 60000;
    const hours = Math.floor(Math.abs(offsetMinutes) / 60);
    const minutes = Math.abs(offsetMinutes) % 60;
    const sign = offsetMinutes >= 0 ? '+' : '-';
    return `(UTC${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')})`;
  } catch {
    return '(UTC+00:00)';
  }
};

const getCurrentTime = (timezone: string): string => {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return formatter.format(now);
  } catch {
    return '';
  }
};

const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

export const TimezoneSelect = ({
  value,
  onChange,
  label,
  placeholder = 'Select timezone...',
  showCurrentTime = false,
  className = '',
  disabled = false,
}: TimezoneSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const browserTimezone = useMemo(() => getBrowserTimezone(), []);

  const filteredTimezones = useMemo(() => {
    if (!search.trim()) return TIMEZONES;
    const searchLower = search.toLowerCase().slice(0, 100);
    return TIMEZONES.filter(
      (tz) =>
        tz.label.toLowerCase().includes(searchLower) ||
        tz.value.toLowerCase().includes(searchLower) ||
        tz.searchTerms.some((term) => term.includes(searchLower))
    );
  }, [search]);

  const groupedTimezones = useMemo(() => {
    const groups: Record<string, TimezoneOption[]> = {
      Americas: [],
      Europe: [],
      Asia: [],
      Australia: [],
      Africa: [],
      Other: [],
    };
    filteredTimezones.forEach((tz) => {
      groups[tz.region].push(tz);
    });
    return groups;
  }, [filteredTimezones]);

  const selectedTimezone = TIMEZONES.find((tz) => tz.value === value);
  const displayText = selectedTimezone
    ? `${selectedTimezone.label} ${getTimezoneOffset(selectedTimezone.value)}`
    : '';

  // Focus search input when popover opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  // Reset search when closed
  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.querySelector(
        `[data-option-index="${highlightedIndex}"]`
      );
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const handleSelect = (timezone: string) => {
    onChange(timezone);
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredTimezones.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredTimezones.length) {
          handleSelect(filteredTimezones[highlightedIndex].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        break;
    }
  };

  return (
    <div className={cn('relative', className)}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      <Popover.Root open={isOpen} onOpenChange={(open) => !disabled && setIsOpen(open)}>
        <Popover.Trigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              'w-full px-4 py-2.5 text-left border border-gray-300 rounded-xl',
              'flex items-center justify-between gap-2',
              disabled
                ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                : 'bg-white hover:border-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
            )}
          >
            <span className={cn('truncate', !displayText && 'text-gray-400')}>
              {displayText || placeholder}
            </span>
            <Icon
              name={ICONS.ANGLE_DOWN}
              size="sm"
              className={cn(
                'flex-shrink-0 transition-transform',
                isOpen && 'rotate-180'
              )}
              ariaHidden
            />
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            className="z-[9999] w-[var(--radix-popover-trigger-width)] bg-white border border-gray-200 rounded-xl shadow-lg"
            sideOffset={4}
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            {/* Search input */}
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Icon
                  name={ICONS.SEARCH}
                  size="sm"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  ariaHidden
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search timezones..."
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Options list */}
            <div
              ref={listRef}
              className="max-h-64 overflow-y-auto overscroll-contain"
              onWheel={(e) => e.stopPropagation()}
            >
              {filteredTimezones.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  {/* eslint-disable-next-line i18next/no-literal-string */}
                  <p className="text-sm">No timezones found</p>
                </div>
              ) : (
                (() => {
                  let optionIndex = 0;
                  return Object.entries(groupedTimezones).map(([region, timezones]) =>
                    timezones.length > 0 ? (
                      <div key={region}>
                        <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 bg-gray-50 sticky top-0">
                          {region}
                        </div>
                        {timezones.map((tz) => {
                          const currentIndex = optionIndex++;
                          const isSelected = value === tz.value;
                          const isHighlighted = currentIndex === highlightedIndex;
                          const isBrowserTimezone = tz.value === browserTimezone;

                          return (
                            <button
                              key={tz.value}
                              type="button"
                              onClick={() => handleSelect(tz.value)}
                              data-option-index={currentIndex}
                              className={cn(
                                'w-full px-3 py-2 text-left transition-colors border-l-2',
                                isSelected && 'bg-primary-50 text-primary-700',
                                isHighlighted && !isSelected && 'bg-gray-100',
                                !isSelected && !isHighlighted && 'hover:bg-gray-50',
                                isBrowserTimezone ? 'border-primary-500' : 'border-transparent'
                              )}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-medium truncate text-sm">
                                  {tz.label}
                                  {isBrowserTimezone && (
                                    // eslint-disable-next-line i18next/no-literal-string
                                    <span className="ml-1.5 text-xs text-primary-600 font-normal">
                                      (Your timezone)
                                    </span>
                                  )}
                                </span>
                                <span className="text-xs text-gray-500 flex-shrink-0">
                                  {getTimezoneOffset(tz.value)}
                                  {showCurrentTime && ` • ${getCurrentTime(tz.value)}`}
                                </span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    ) : null
                  );
                })()
              )}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};
